import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';
import { Bar, BarChart, Cell, Customized, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { getPerformanceChartChrome, getPerformanceTooltipStyle } from '../../styles/performanceMsiStyles';
import { stepExtent, waterfallDomain } from './exploreColumns';
import { formatPercent } from './exploreFormat';

const ROW_HEIGHT = 28;
export const NAME_WIDTH = 124;
const NAME_CHARACTERS = 19;
const LABEL_GAP = 4;
// Wider than any character of the 11px value labels, so a gutter sized from
// it always holds the longest label.
const LABEL_CHARACTER_WIDTH = 7;

export const formatStep = (value) => formatPercent(value, 2);

/**
 * The px gutter kept free on each side of the bars. The bars never enter it
 * and each value label sits just past its bar's end, so the longest label
 * still ends inside the plot and never reaches the factor names on the left.
 */
export function valueLabelGutter(steps, format = formatStep) {
    const longest = Math.max(...steps.map((step) => format(step.size).length));
    return Math.ceil(longest * LABEL_CHARACTER_WIDTH) + LABEL_GAP * 2;
}

// A label goes on the side its step moves: left of a loss, right of a gain,
// past the total's range whisker.
export function valueLabelPlacement(step, scale) {
    const [lo, hi] = stepExtent(step);
    return step.size < 0
        ? { x: scale(lo) - LABEL_GAP, anchor: 'end' }
        : { x: scale(hi) + LABEL_GAP, anchor: 'start' };
}

function FactorName({ x, y, payload, steps, fill }) {
    const step = steps.find((item) => item.key === payload?.value);
    if (!step) return null;
    const short = step.label.length > NAME_CHARACTERS ? `${step.label.slice(0, NAME_CHARACTERS - 1)}…` : step.label;
    return (
        <text x={x} y={y} dy="0.35em" textAnchor="end" fontSize={11} fill={fill} fontWeight={step.total ? 700 : 500}>
            <title>{step.family ? `${step.label} (${step.family})` : step.label}</title>
            {short}
        </text>
    );
}

// Value labels and the total's whisker, drawn with the chart's own scales.
function WaterfallMarks({ xAxisMap, yAxisMap, steps, ink }) {
    const xAxis = xAxisMap && Object.values(xAxisMap)[0];
    const yAxis = yAxisMap && Object.values(yAxisMap)[0];
    if (!xAxis?.scale || !yAxis?.scale) return null;
    const band = typeof yAxis.scale.bandwidth === 'function' ? yAxis.scale.bandwidth() : 0;
    return (
        <g className="factor-waterfall-marks">
            {steps.map((step) => {
                const top = yAxis.scale(step.key);
                if (!Number.isFinite(top)) return null;
                const y = top + band / 2;
                const placement = valueLabelPlacement(step, xAxis.scale);
                const whisker = step.total && Number.isFinite(step.range)
                    ? [xAxis.scale(step.end - step.range), xAxis.scale(step.end + step.range)] : null;
                return (
                    <g key={step.key}>
                        {whisker && (
                            <g stroke={ink} strokeWidth={1.5}>
                                <line x1={whisker[0]} x2={whisker[1]} y1={y} y2={y} />
                                <line x1={whisker[0]} x2={whisker[0]} y1={y - 5} y2={y + 5} />
                                <line x1={whisker[1]} x2={whisker[1]} y1={y - 5} y2={y + 5} />
                            </g>
                        )}
                        <text x={placement.x} y={y} dy="0.35em" textAnchor={placement.anchor} fontSize={11} fill={ink} fontWeight={step.total ? 700 : 500}>
                            {formatStep(step.size)}
                        </text>
                    </g>
                );
            })}
        </g>
    );
}

function StepTooltip({ active, payload, style }) {
    const step = active ? payload?.[0]?.payload : null;
    if (!step) return null;
    return (
        <div style={style}>
            <strong>{step.label}</strong>
            {step.family && <div>{step.family}</div>}
            <div>Contribution {formatPercent(step.size, 2)}</div>
            {Number.isFinite(step.exposure) && <div>Net exposure {step.exposure.toFixed(2)}</div>}
            {Number.isFinite(step.factorReturn) && <div>Factor return {formatPercent(step.factorReturn, 2)}</div>}
            {step.total && Number.isFinite(step.range) && <div>Stock-specific range ±{(step.range * 100).toFixed(2)}%</div>}
        </div>
    );
}

/**
 * Where a result comes from, factor by factor: each bar starts where the
 * previous one ended (a transparent base stacked under the signed
 * contribution), ending at the total with its stock-specific whisker.
 */
export default function FactorWaterfall({ steps }) {
    const theme = useTheme();
    const chrome = getPerformanceChartChrome(theme);
    const domain = useMemo(() => waterfallDomain(steps), [steps]);
    const gutter = valueLabelGutter(steps);
    const summary = steps.map((step) => `${step.label} ${formatStep(step.size)}`).join(', ');
    return (
        <Box role="img" aria-label={`Factor contributions: ${summary}`} sx={{ width: '100%', height: steps.length * ROW_HEIGHT + 8 }}>
            <ResponsiveContainer>
                <BarChart data={steps} layout="vertical" margin={{ top: 4, right: 4, bottom: 4, left: 0 }} barCategoryGap={7}>
                    <XAxis type="number" domain={domain} hide padding={{ left: gutter, right: gutter }} allowDataOverflow />
                    <YAxis type="category" dataKey="key" width={NAME_WIDTH} interval={0} tickLine={false} axisLine={false}
                        tick={<FactorName steps={steps} fill={chrome.secondaryText} />} />
                    <ReferenceLine x={0} stroke={chrome.axis} />
                    <ChartTooltip cursor={{ fill: chrome.inset }} content={<StepTooltip style={getPerformanceTooltipStyle(theme)} />} />
                    <Bar dataKey="base" stackId="waterfall" fill="transparent" isAnimationActive={false} />
                    <Bar dataKey="size" stackId="waterfall" isAnimationActive={false} radius={2}>
                        {steps.map((step) => (
                            <Cell key={step.key} fill={step.total ? chrome.secondaryText
                                : step.size >= 0 ? 'rgb(var(--c-positive, 46 125 50))' : 'rgb(var(--c-negative, 198 40 40))'} />
                        ))}
                    </Bar>
                    <Customized component={<WaterfallMarks steps={steps} ink={chrome.text} />} />
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}

FactorWaterfall.propTypes = {
    steps: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        base: PropTypes.number.isRequired,
        size: PropTypes.number.isRequired,
        end: PropTypes.number.isRequired,
    })).isRequired,
};
